// src/autodesk_usage/hooks/useCorporateProductUsage.ts
// All comments in English only
import { useQuery } from "@tanstack/react-query";
import {
  getCorporateProductUsageSummary,
  type CorporateProductUsageSummary,
} from "../api/corporate";

export interface UseCorporateProductUsageResult {
  data?: CorporateProductUsageSummary[];
  isLoading: boolean;
  error: unknown;
}

function normalizeCorporateNames(value?: string | string[] | null) {
  const list = Array.isArray(value) ? value : value ? [value] : [];
  return Array.from(
    new Set(list.map((item) => item.trim()).filter(Boolean))
  ).sort();
}

function normalizeProductUsagePayload(
  payload: unknown
): CorporateProductUsageSummary[] {
  if (Array.isArray(payload)) {
    return payload as CorporateProductUsageSummary[];
  }
  if (
    payload &&
    typeof payload === "object" &&
    Array.isArray((payload as any).items)
  ) {
    return (payload as any).items as CorporateProductUsageSummary[];
  }
  return [];
}

export function useCorporateProductUsage(
  groupName?: string | string[] | null
): UseCorporateProductUsageResult {
  const corporateNames = normalizeCorporateNames(groupName);
  const corporateKey = corporateNames.join("|");
  const query = useQuery<CorporateProductUsageSummary[]>({
    queryKey: ["corporate-product-usage", corporateKey],
    queryFn: async () => {
      if (!corporateNames.length) return [];
      const res = await getCorporateProductUsageSummary(corporateNames);
      return normalizeProductUsagePayload(res.data);
    },
    enabled: corporateNames.length > 0,
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
  };
}
